import { T, useT } from '@transifex/react';
import { isRouteErrorResponse, type ErrorResponse } from 'react-router';

import { Button } from './ui/button';
import { BackToDashboardButton, ErrorPageActions, ErrorPageLayout } from './error-page-layout';

type ServerErrorPageProps = {
  error?: Error | Response | ErrorResponse;
};

export function ServerErrorPage({ error }: ServerErrorPageProps) {
  const t = useT();
  const isDev = import.meta.env.DEV;
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <ErrorPageLayout
      title={t('Something went wrong')}
      description={t('Sorry, we are working hard to fix the issue for you.')}
      actions={
        <ErrorPageActions>
          <Button variant="outline" onClick={handleRefresh}>
            <T _str="Refresh the page" />
          </Button>
          <BackToDashboardButton />
        </ErrorPageActions>
      }
      image={<img src="/assets/server-error.svg" alt={t('Server Error')} className="max-w-full" />}
      debugInfo={
        isDev && error ? (
          <div className="mt-lg w-full max-w-4xl">
            <div className="rounded-lg border border-red-200 bg-red-50 p-md">
              <h3 className="mb-sm text-body-big-bold text-red-800">Development Error Details</h3>

              {isRouteErrorResponse(error) ? (
                <>
                  <p className="mb-sm text-body-big text-red-700">
                    <strong>Handled Status:</strong> {error.status}
                  </p>
                  {error.statusText && (
                    <ErrorDetail title="Original Status Text" content={error.statusText} />
                  )}
                  {error.data && <ErrorDetail title="Handled Response Data" content={error.data} />}
                </>
              ) : error instanceof Error ? (
                <>
                  <p className="mb-sm text-body-big text-red-700">{error.message}</p>
                  {error.stack && (
                    <pre
                      className="
                        overflow-x-auto rounded-sm bg-red-100 p-sm text-xs
                        text-red-600
                      "
                    >
                      <code>{error.stack}</code>
                    </pre>
                  )}
                </>
              ) : (
                <p className="text-body-big text-red-700">Unknown error type</p>
              )}
            </div>
          </div>
        ) : undefined
      }
    />
  );
}

function ErrorDetail({ title, content }: { title: string; content: string }) {
  return (
    <div className="mb-sm">
      <strong className="text-red-800">{title}</strong>
      <pre
        className="
          mt-xs overflow-x-auto rounded-sm bg-red-100 p-sm text-xs text-red-600
        "
      >
        <code>{content}</code>
      </pre>
    </div>
  );
}
